/**
 * İngilizce Çeviri Dosyası
 *
 * NOT: Bu projenin yazılımcısının ana dili Türkçe'dir.
 * Bu dosya Türkçe çeviri dosyasından (tr.ts) çevrilmiştir.
 * Ana kaynak Türkçe çeviri dosyasıdır.
 */
export const en = {
  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    sort: 'Sort',
    sortAsc: 'Sort ascending',
    sortDesc: 'Sort descending',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    view: 'View',
    copy: 'Copy',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    actions: 'Actions',
    code: 'Code',
    all: 'All',
    clear: 'Clear',
    filter: 'Filter',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    close: 'Close',
    select: 'Select',
    active: 'Active',
    inactive: 'Inactive',
    success: 'Success',
    updated: 'Updated',
    created: 'Created',
    deleted: 'Deleted',
    confirmDelete: 'Are you sure you want to delete?',
    errorOccurred: 'An error occurred',
    noData: 'No data',
    status: 'Status',
    show: 'Show',
    hide: 'Hide',
    searchPlaceholder: 'Search...',
    allStatuses: 'All Statuses',
    clearFilters: 'Clear Filters',
    newRecord: 'New Record',
    noPermission: 'You do not have permission',
    statusDistribution: 'Status Distribution',
    rows: 'Rows',
    firstPage: 'First Page',
    previousPage: 'Previous Page',
    nextPage: 'Next Page',
    lastPage: 'Last Page',
    page: 'Page',
    optional: 'Optional',
    saving: 'Saving...',
    deleteConfirmTitle: 'Confirm Delete',
    deleteWarning: 'This action cannot be undone!',
    autoTranslate: 'Auto Translate',
    autoTranslateSuccessCount: '{count} entries auto-translated.',
    autoTranslateNoSource: 'No source translations found.',
    enterTargetLanguage: 'Select target language.',
    sourceLanguageLabel: 'Source Language (your UI language)',
    targetLanguage: 'Target Language',
    sourceAndTargetMustDiffer: 'Source and target languages must differ.',
    context: 'Table',
    key: 'Column',
    value: 'Value',
    locale: 'Language',
    auto: 'Auto',
    manual: 'Manual',
    manageLocales: 'Manage Languages',
    activeLocalesDescription: 'Select active languages. Saved as preference only.',
    updateUntranslatedFields: 'Sync New Fields',
    syncEntriesResult: '{count} new translation entries synced.',
    syncDeletedResult: '{count} orphaned entries removed.',
    selectSourceLanguageForSync: 'In which language is the content?',
    sourceLanguage: 'Source Language',
    originalShort: 'Orig.',
    languageNames: { en: 'English', tr: 'Turkish', de: 'German', fr: 'French', es: 'Spanish', ar: 'Arabic', it: 'Italian', ja: 'Japanese', zh: 'Chinese', ru: 'Russian' } as Record<string, string>,
    perPage: 'Per Page',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    copyright: '© {year} QubePro. All rights reserved.',
    translationRates: 'Translation Rates',
    profileMenu: 'Profile Menu',
    manageYourAccount: 'Manage your account',
    adminConsole: 'Admin Console',
    serverConfiguring: 'Server is being configured, please wait...',
    productName: 'Management',
  },

  // Portal
  portal: {
    searchPlaceholder: 'Search apps...',
    noAppsFound: 'No apps found.',
    quickLinks: 'Quick Links',
    console: 'Admin Console',
    lastLogin: 'Last Login',
    apps: {
      core: { name: 'Management', description: 'Manage companies, organizations, users, roles and permissions in a central platform.' },
      bi: { name: 'Analytics', description: 'Build reports and dashboards from your data. Turn business data into insights and decisions.' },
      bpm: { name: 'Workflows', description: 'Start, track and complete internal requests, approvals and task processes in a digital environment.' },
      support: { name: 'Support', description: 'Handle customer requests and support tickets. Track and resolve issues in one place.' },
      agent: { name: 'Automation', description: 'Automate repetitive tasks with bots and agents. Integrate with your existing tools.' },
      ai: { name: 'AI & Predictions', description: 'Use AI models for data analysis and prediction. Improve planning and decisions.' },
      erp: { name: 'Business Suite', description: 'Integrated modules for finance, sales, warehouse and operations. Run your business on one platform.' },
      pms: { name: 'Projects', description: 'Plan projects, assign tasks, track progress and collaborate with your team in one place.' },
      crm: { name: 'Customer Relations', description: 'Manage contacts, leads, opportunities and sales pipeline. All customer info and history in one place.' },
    },
    menus: {
      core_dashboard: 'Dashboard',
      core_companies: 'Companies',
      core_organizations: 'Organizations',
      core_roles: 'Roles',
      core_users: 'Users',
      core_groups: 'Groups',
      core_emails: 'Email Accounts',
      core_data_sources: 'Data Sources',
      core_settings_company: 'Company Settings',
      core_settings_user: 'User Settings',
      core_translations: 'Translations',
    },
  },

  // Panel (ERP home page module list)
  panel: {
    apps: {
      crm: { name: 'CRM', footerName: 'CRM' },
      accounting: { name: 'Accounting' },
      finance: { name: 'Finance' },
      procurement: { name: 'Procurement' },
      inventory: { name: 'Inventory' },
      purchase: { name: 'Purchase' },
      sales: { name: 'Sales' },
      manufacturing: { name: 'Manufacturing' },
      assets: { name: 'Assets' },
      hr: { name: 'HR' },
      project: { name: 'Project Management' },
      timesheet: { name: 'Timesheets' },
      documents: { name: 'Documents' },
      pos: { name: 'Point of Sale' },
      rental: { name: 'Rental' },
      dashboard: { name: 'Reports' },
    },
    appsLabel: 'Applications',
    settingsLabel: 'Settings',
    coreLabel: 'Management',
    backToHome: 'Back to Home',
  },

  // Errors
  errors: {
    unknown: 'An unknown error occurred',
    network: 'Connection to server failed. Please check your internet connection.',
    timeout: 'Request timed out. Please try again.',
    badRequest: 'Invalid request',
    unauthorized: 'Your session has expired. Please log in again.',
    forbidden: 'You do not have permission for this action.',
    notFound: 'The requested resource was not found',
    tooManyRequests: 'Too many requests sent. Please try again later.',
    internalServerError: 'A server error occurred. Please try again later.',
    badGateway: 'Server temporarily unavailable',
    serviceUnavailable: 'Service temporarily unavailable',
    gatewayTimeout: 'Server is not responding',
    serverErrorWithStatus: 'Server error ({status})',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Central Management System Summary',
    welcome: 'Welcome',
    guestName: 'User',
    statusSummary: 'Your current status overview',
    currentTimeLabel: 'Current Time',
    todayLabel: 'Today',
    activeCompanies: 'Active Companies',
    activeOrganizations: 'Active Organizations',
    activeUsers: 'Active Users',
    activeRoles: 'Active Roles',
    database: 'Database',
    databaseUsed: 'Storage Used',
    recentlyAddedUsers: 'Recently Added Users',
    recentlyAddedCompanies: 'Recently Added Companies',
    noUsersYet: 'No users yet',
    noCompaniesYet: 'No companies yet',
    userPanel: 'User Panel',
    userPanelWelcome: 'Welcome! You can access your user settings from the menu.',
    quickActions: 'Quick Actions',
    systemHealthTitle: 'System Health',
    systemLogsTitle: 'System Logs',
    logChartTitle: 'Log Error/Warning Breakdown',
    logErrorsLabel: 'Errors',
    logWarningsLabel: 'Warnings',
    logSuccessLabel: 'Success',
    statusHealthy: 'Healthy',
    statusFailed: 'Failed',
    statusUnknown: 'Unknown',
    lastRun: 'Last Run',
    failures24h: '24h Failures',
    noData: 'No data',
    systemComponents: {
      EMAIL_INGEST: 'Inbound Mail (IMAP)',
      EMAIL_DISPATCH: 'Outbound Mail (SMTP)',
      JOB_SCHEDULER: 'Scheduler',
      API: 'API',
    },
  },

  // Sidebar
  sidebar: {
    dashboard: 'Dashboard',
    roles: 'Roles',
    users: 'Users',
    settings: 'Settings',
    systemSettings: 'System Settings',
    userSettings: 'User Settings',
    organizations: 'Organizations',
    logout: 'Logout',
    toggleMenu: 'Open Menu',
    toggleMenuClose: 'Close Menu',
    management: 'Management',
    companies: 'Companies',
    emailAccounts: 'Email Accounts',
    dataSources: 'Data Sources',
    translations: 'Translations',
  },

  // Settings
  settings: {
    title: 'QubePro ERP',
    subtitle: 'Manage company and user settings',
    companySettingsSubtitle: 'Manage general company settings here',
    userSettingsSubtitle: 'Manage your user settings here',
    securitySettingsSubtitle: 'Manage password and security settings here',
    companySettings: 'Company Settings',
    userSettings: 'User Settings',
    general: 'General Settings',
    personal: 'Personal Settings',
    timezone: 'Timezone',
    dateFormat: 'Date Format',
    defaultCurrency: 'Default Currency',
    defaultLanguage: 'Default Language',
    language: 'Language',
    companySettingsSaved: 'Company settings saved successfully',
    userSettingsSaved: 'User settings saved successfully',
    failedToLoad: 'Failed to load settings',
    saveFailed: 'Failed to save settings',
    profilePicture: 'Profile Picture',
    maxFileSize: 'Max file size: 5 MB. Formats: JPEG, PNG, GIF, WebP',
    change: 'Change',
    uploading: 'Uploading...',
    deleteAvatar: 'Delete Profile Picture',
    deleteAvatarConfirm: 'Are you sure you want to delete your profile picture?',
    avatarUploaded: 'Profile picture uploaded',
    avatarDeleted: 'Profile picture deleted',
    avatarUploadError: 'Error uploading profile picture',
    avatarDeleteError: 'Error deleting profile picture',
    onlyImageFiles: 'Only image files allowed',
    fileSizeError: 'File size must be under 5 MB',
    appearance: 'Appearance',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    pageSize: 'Table Page Size',
    defaultCompany: 'Default Company',
    selectDefaultCompany: 'Select default company',
    noCompaniesAssigned: 'No companies assigned',
    security: 'Security',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password',
    passwordMismatch: 'New passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    passwordSameAsOld: 'New password cannot be the same as current',
    changingPassword: 'Changing password...',
  },

  // Organizations
  organizations: {
    title: 'Organizations',
    code: 'Code',
    name: 'Organization Name',
    description: 'Description',
    logo: 'Logo',
    logoEmpty: 'No Logo',
    uploadLogo: 'Upload Logo',
    status: 'Status',
    smtp: 'SMTP',
    imap: 'IMAP',
    smtpEmailAccount: 'SMTP Email Account',
    imapEmailAccount: 'IMAP Email Account',
    active: 'Active',
    inactive: 'Inactive',
    newOrganization: 'New Organization',
    editOrganization: 'Edit Organization',
    deleteConfirm: 'Are you sure you want to delete this organization?',
    deleteFailed: 'Failed to delete organization',
    operationFailed: 'Operation failed',
    failedToLoad: 'Failed to load organizations',
    dataFormatError: 'Organization data in unexpected format',
    searchPlaceholder: 'Search organization (name, description)',
    empty: 'No data',
    notSet: 'Not selected',
    noCreatePermission: 'No permission to create organizations',
    noUpdatePermission: 'No permission to update organizations',
    noDeletePermission: 'No permission to delete organizations',
    company: 'Company Name',
    statusDistribution: 'Organization Status Distribution',
  },

  // Users
  users: {
    title: 'Users',
    newUser: 'New User',
    editUser: 'Edit User',
    noUsersYet: 'No data',
    failedToLoad: 'Failed to load users',
    deleteConfirm: 'Are you sure you want to delete this user?',
    deleteFailed: 'Failed to delete user',
    operationFailed: 'Operation failed',
    passwordRequired: 'Password required',
    passwordChangeHint: '(Fill in to change)',
    fullName: 'Full Name',
    email: 'Email',
    status: 'Status',
    roles: 'Roles',
    actions: 'Actions',
    firstName: 'First Name *',
    lastName: 'Last Name *',
    password: 'Password',
    noRoles: 'No roles',
    rolesNotFound: 'No roles found',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusSuspended: 'Suspended',
    userInfoFailed: 'Failed to load user info',
    rolesFailed: 'Failed to load roles',
    copy: 'Copy',
    isAdmin: 'Admin',
    groups: 'Groups',
    groupCount: 'Group Count',
    userGroupName: 'Group Name',
    userGroupCode: 'Group Code',
    userGroupEmail: 'Group Email',
    groupManager: 'Group Manager',
    members: 'Members',
    userGroupDescription: 'Description',
    userGroupCreate: 'Create New Group',
    userGroupEdit: 'Edit Group',
    manageMembers: 'Manage Members',
    addMember: 'Add Member',
    memberCount: 'Member Count',
    searchUsers: 'Search users...',
    noMembers: 'No members yet',
    selectUser: 'Select user',
    addUser: 'Add',
    removeUser: 'Remove',
    manageGroups: 'Manage Groups',
    noUsersFound: 'No data',
    statusDistribution: 'User Status Distribution',
    groupStatusDistribution: 'Group Status Distribution',
  },

  // Roles
  roles: {
    title: 'Roles',
    newRole: 'New Role',
    editRole: 'Edit Role',
    noRolesYet: 'No data',
    failedToLoad: 'Failed to load roles',
    deleteConfirm: 'Are you sure you want to delete this role?',
    deleteFailed: 'Failed to delete role',
    operationFailed: 'Operation failed',
    dataFormatError: 'Data format error',
    permissionsFailed: 'Failed to load permissions',
    name: 'Role Name *',
    description: 'Description',
    permissions: 'Permissions',
    permissionCount: 'Permission Count',
    type: 'Role Type',
    typeSystem: 'System',
    typeCustom: 'Custom',
    typeAll: 'All Roles',
    searchPlaceholder: 'Search (role name, description, permission)...',
    systemBadge: 'System',
    permissionsCount: 'Permissions ({count})',
    morePermissions: '+{count} more',
    copy: 'Copy',
    copySuffix: ' (Copy)',
    clearFilters: 'Clear Filters',
    noCreatePermission: 'No permission to create roles',
    noUpdatePermission: 'No permission to update roles',
    noDeletePermission: 'No permission to delete roles',
    systemRoleEditBlocked: 'System role cannot be edited',
    systemRoleDeleteBlocked: 'System role cannot be deleted',
    noPermissions: 'No permissions',
    paginationRange: '{start}-{end} of {total}',
    paginationPage: 'Page {page} / {total}',
    modules: {
      users: 'Users',
      groups: 'Groups',
      userGroupName: 'Group Name',
      userGroupCode: 'Group Code',
      groupManager: 'Group Manager',
      members: 'Members',
      userGroupDescription: 'Description',
      userGroupCreate: 'Create New Group',
      userGroupEdit: 'Edit Group',
      roles: 'Roles',
      tenant: 'Tenant',
      userSettingField: 'Setting',
      settings: 'Settings',
      organizations: 'Organizations',
    },
    groups: 'Groups',
    userGroup: 'Group',
    userGroupList: 'Group List',
    userGroupCreate: 'Create Group',
    userGroupEdit: 'Edit Group',
    userGroupName: 'Group Name',
    userGroupDescription: 'Description',
    userGroupCode: 'Code',
    groupManager: 'Group Manager',
    members: 'Members',
    addMember: 'Add Member',
    assignRole: 'Assign Role',
    assign: 'Assign',
    statusDistribution: 'Role Status Distribution',
    assignRoleMatrix: 'Role Matrix',
    roleMatrix: 'Role Matrix',
    selectCompanyToViewMatrix: 'Select company to view matrix',
    show: 'Show',
    hide: 'Hide',
  },

  // Companies
  companies: {
    title: 'Companies',
    newCompany: 'New Company',
    editCompany: 'Edit Company',
    noCompaniesYet: 'No data',
    failedToLoad: 'Failed to load companies',
    deleteConfirm: 'Are you sure you want to delete this company?',
    deleteFailed: 'Failed to delete company',
    operationFailed: 'Operation failed',
    dataFormatError: 'Data format error',
    name: 'Company Name *',
    status: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    searchPlaceholder: 'Search (company name, status)...',
    clearFilters: 'Clear Filters',
    copy: 'Copy',
    copySuffix: ' (Copy)',
    noCreatePermission: 'No permission to create companies',
    noUpdatePermission: 'No permission to update companies',
    noDeletePermission: 'No permission to delete companies',
    paginationRange: '{start}-{end} of {total}',
    paginationPage: 'Page {page} / {total}',
    allStatuses: 'All Statuses',
    statusDistribution: 'Company Status Distribution',
    show: 'Show',
    hide: 'Hide',
    logo: 'Logo',
    logoEmpty: 'No Logo',
    uploadLogo: 'Upload Logo',
    smtpEmailAccount: 'SMTP Email Account',
    imapEmailAccount: 'IMAP Email Account',
    smtpEmailId: 'SMTP Email Account',
    imapEmailId: 'IMAP Email Account',
  },

  // Emails
  emails: {
    title: 'Email Accounts',
    newEmail: 'New Email Account',
    editEmail: 'Edit Email Account',
    noEmailsYet: 'No data',
    failedToLoad: 'Failed to load email accounts',
    deleteConfirm: 'Are you sure you want to delete this email account?',
    deleteConfirmMessage: 'Are you sure you want to delete email account "{name}"?',
    deleteFailed: 'Failed to delete email account',
    operationFailed: 'Operation failed',
    dataFormatError: 'Data format error',
    name: 'Account Name',
    type: 'Type',
    host: 'Host',
    port: 'Port',
    user: 'Username',
    password: 'Password',
    fromEmail: 'From Email',
    secure: 'Secure (SSL/TLS)',
    secureConnection: 'Secure Connection (SSL/TLS)',
    status: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    isActive: 'Active',
    searchPlaceholder: 'Search (name, host, user)...',
    clearFilters: 'Clear Filters',
    copy: 'Copy',
    noCreatePermission: 'No permission to create email accounts',
    noUpdatePermission: 'No permission to update email accounts',
    noDeletePermission: 'No permission to delete email accounts',
    paginationRange: '{start}-{end} of {total}',
    paginationPage: 'Page {page} / {total}',
    allStatuses: 'All Statuses',
    allTypes: 'All Types',
    statusDistribution: 'Account Status Distribution',
    show: 'Show',
    hide: 'Hide',
    nameRequired: 'Account name required',
    hostRequired: 'Host required',
    portRequired: 'Port required',
    userRequired: 'Username required',
    passwordRequired: 'Password required',
    leaveBlankToKeep: 'Leave blank to keep unchanged',
    noData: 'No data',
    noEmailsMessage: 'No data',
    passwordEditHint: 'Leave blank to keep unchanged',
    noSearchResults: 'No data',
    namePlaceholder: 'e.g. Company Support or info@company.com',
    deleteConfirmTitle: 'Delete Account',
  },

  // DataSources
  dataSources: {
    title: 'Data Sources',
    subtitle: 'Manage customer database connections',
    newDataSource: 'New Data Source',
    editDataSource: 'Edit Data Source',
    noDataSourcesYet: 'No data',
    failedToLoad: 'Failed to load data sources',
    deleteConfirm: 'Are you sure you want to delete this data source?',
    deleteFailed: 'Failed to delete data source',
    operationFailed: 'Operation failed',
    dataFormatError: 'Data format error',
    name: 'Display Name',
    driver: 'Driver',
    server: 'Server',
    port: 'Port',
    databaseName: 'Database Name',
    username: 'Username',
    password: 'Password',
    schema: 'Schema',
    sourceType: 'Source Type',
    source: 'Source',
    target: 'Target',
    sourceSystem: 'Source System',
    targetSystem: 'Target System',
    status: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    searchPlaceholder: 'Search (name, server, database)...',
    clearFilters: 'Clear Filters',
    copy: 'Copy',
    paginationRange: '{start}-{end} / {total}',
    paginationPage: 'Page {page} / {total}',
    allStatuses: 'All Statuses',
    allTypes: 'All',
    statusDistribution: 'Data Source Status Distribution',
    show: 'Show',
    hide: 'Hide',
    nameRequired: 'Display name required',
    serverRequired: 'Server address required',
    databaseRequired: 'Database name required',
    passwordRequired: 'Password required',
    testConnection: 'Test Connection',
    testing: 'Testing...',
    testSuccess: 'Connection successful!',
    testFailed: 'Connection failed: ',
    integratedSecurity: 'Integrated Security',
    ssl: 'SSL',
    connectionTimeout: 'Connection Timeout (sec)',
    description: 'Description',
    noCreatePermission: 'No permission to create data sources',
    noUpdatePermission: 'No permission to update data sources',
    noDeletePermission: 'No permission to delete data sources',
  },

  // AI Providers
  aiProviders: {
    title: 'AI Providers',
    subtitle: 'Manage AI models and integrations',
    newProvider: 'New AI Provider',
    editProvider: 'Edit AI Provider',
    noProvidersYet: 'No data',
    failedToLoad: 'Failed to load AI providers',
    deleteConfirm: 'Are you sure you want to delete this AI provider?',
    deleteFailed: 'Failed to delete AI provider',
    operationFailed: 'Operation failed',
    name: 'Provider Name *',
    provider: 'Provider Type',
    baseUrl: 'Base URL',
    apiKey: 'API Key',
    description: 'Description',
    status: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    searchPlaceholder: 'Search (name, type, url)...',
    clearFilters: 'Clear Filters',
    noCreatePermission: 'You do not have permission to create AI providers',
    statusDistribution: 'AI Provider Status Distribution',
    baseUrlPlaceholder: 'e.g., http://localhost:11434, https://api.custom.ai/v1',
    baseUrlEmptyDefault: 'Leave empty for defaults',
    apiKeyPlaceholder: 'Enter API Key',
  },

  // Login
  login: {
    title: 'QubePro ERP',
    subtitle: 'Central Management System',
    heroTitle: 'Operate enterprise-grade management from a single control center',
    heroSubtitle: 'QubePro ERP unifies company and organization management, user and role management within one secure platform.',
    languageToggleLabel: 'Language selection',
    turkish: 'Turkish',
    english: 'English',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'example@domain.com',
    emailHint: 'Tenant is automatically determined by domain',
    passwordPlaceholder: '••••••••',
    loginButton: 'Login',
    loggingIn: 'Logging in...',
    loginFailed: 'Login failed',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    featureTitle: 'Enterprise Resource Planning',
    featureDescription: 'Manage your customer relationships, sales, inventory, and financial processes in one integrated platform.',
    featureCard1Title: 'Customer Management',
    featureCard1Description: 'Track your customers, potential sales, and business relationships from a central location.',
    featureCard2Title: 'Sales and Orders',
    featureCard2Description: 'Easily manage quotes, orders, and sales processes to drive revenue growth.',
    featureCard3Title: 'Reports and Analytics',
    featureCard3Description: 'Monitor business performance with real-time reports and dashboards to make data-driven decisions.',
    featureCard4Title: 'Integration and Settings',
    featureCard4Description: 'Customize your system, integrate with other tools, and automate your business processes.',
    pleaseWait: 'Please Wait',
    serverConfiguring: 'Server is being configured, please wait...',
    dbCreating: 'Database and required configurations are being created. This process may take a few seconds.',
    loginPageTitle: 'Login',
    submitButton: 'Login',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    emailPlaceholderFull: 'Enter your email address',
    passwordPlaceholderFull: 'Enter password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    emailRequired: 'Email address is required',
    passwordRequired: 'Password is required',
    backendUnavailable: 'Backend server is currently unavailable. Please try again later.',
    backendConnectFailed: 'Cannot connect to backend server. Please try again later.',
    backendConnectFailedDev: 'Cannot connect to backend server. Please ensure the backend server is running ({apiUrl}).',
    fieldEmail: 'Email',
    fieldPassword: 'Password',
    errors: {
      invalidCredentials: 'Invalid email or password. Please try again.',
      databaseUnavailable: 'Database unavailable or setup required. Please log in again.',
      accountInactive: 'Your account is not active. Please contact your system administrator.',
      sessionExpired: 'Your session has expired. Please log in again.',
      tooManyAttempts: 'Too many attempts. Please try again later.',
      sessionTooLarge: 'Session data is too large. Please log in again.',
    },
  },

};
